import { RSocket, RSocketConnector } from "rsocket-core";
import { WebsocketClientTransport } from "rsocket-websocket-client";
import { ConnectionStatus } from "../types";
import { RSocketConnectionManager, RSocketConnectionOptions } from "./utils";

/**
 * Creates a functional RSocket connection manager
 * @param url WebSocket URL to connect to
 * @param options Optional connection options
 * @returns RSocket connection manager
 */
export function createRSocketConnectionManager(url: string, options: RSocketConnectionOptions = {}): RSocketConnectionManager {
    let socket: RSocket | null = null;
    let connectionStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;
    let webSocketInstance: WebSocket | null = null;

    const config = {
        maxReconnectAttempts: options.maxReconnectAttempts ?? 1800,
        reconnectDelay: options.reconnectDelay ?? 2000,
        onStatusChange: options.onStatusChange ?? null,
        onError: options.onError ?? null,
    };

    const connector = new RSocketConnector({
        setup: {
            keepAlive: 10_000,
            lifetime: 90_000,
            dataMimeType: "application/json",
            metadataMimeType: "message/x.rsocket.composite-metadata.v0",
        },
        transport: new WebsocketClientTransport({
            url,
            wsCreator: (wsUrl) => {
                const ws = new WebSocket(wsUrl);
                webSocketInstance = ws;

                ws.addEventListener("open", () => {
                    console.debug(`WebSocket connection opened to ${wsUrl}`);
                });

                ws.addEventListener("error", (event) => {
                    console.error("WebSocket error:", event);
                });

                ws.addEventListener("close", (event) => {
                    console.debug(`WebSocket connection closed: code=${event.code}, reason=${event.reason}`);
                });

                return ws;
            },
        }),
    });

    const updateStatus = (status: ConnectionStatus): void => {
        connectionStatus = status;
        if (config.onStatusChange) {
            config.onStatusChange(status);
        }
    };

    const handleError = (error: Error | Event | undefined): void => {
        let normalizedError: Error;

        if (error instanceof Error) {
            normalizedError = error;
        } else {
            normalizedError = new Error(error ? `WebSocket error: ${JSON.stringify(error)}` : "Unknown WebSocket error");
        }

        console.error("RSocket connection error:", normalizedError);
        updateStatus(ConnectionStatus.ERROR);

        if (config.onError) {
            config.onError(normalizedError);
        }

        scheduleReconnect();
    };

    const scheduleReconnect = (): void => {
        if (reconnectTimer !== null) {
            clearTimeout(reconnectTimer);
        }

        if (reconnectAttempts < config.maxReconnectAttempts) {
            reconnectAttempts++;
            const delay = config.reconnectDelay * Math.pow(1.5, reconnectAttempts - 1);

            console.debug(`Scheduling reconnect attempt ${reconnectAttempts} in ${delay}ms`);
            reconnectTimer = setTimeout(() => {
                console.debug(`Attempting reconnect ${reconnectAttempts}/${config.maxReconnectAttempts}`);
                connect().catch((connectError) => {
                    console.error("Reconnection failed:", connectError);
                    scheduleReconnect();
                });
            }, delay);
        } else {
            console.error(`Max reconnection attempts (${config.maxReconnectAttempts}) reached`);
        }
    };

    const getStatus = (): ConnectionStatus => {
        return connectionStatus;
    };

    const isConnected = (): boolean => {
        return connectionStatus === ConnectionStatus.CONNECTED && socket !== null;
    };

    const connect = async (): Promise<void> => {
        if (isConnected()) {
            console.debug("Already connected");
            return;
        }

        // Clear any existing reconnect timer
        if (reconnectTimer !== null) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }

        try {
            updateStatus(ConnectionStatus.CONNECTING);
            console.debug(`Connecting to RSocket server at ${url}`);

            const connectionTimeout = new Promise<never>((_, reject) => {
                setTimeout(() => {
                    reject(new Error(`Connection timeout after 30000ms to ${url}`));
                }, 30000);
            });

            socket = await Promise.race([connector.connect(), connectionTimeout]);

            reconnectAttempts = 0;
            updateStatus(ConnectionStatus.CONNECTED);
            console.debug("RSocket connection established");

            socket.onClose(() => {
                console.debug("RSocket connection closed");

                socket = null;
                updateStatus(ConnectionStatus.DISCONNECTED);
                scheduleReconnect();
            });
        } catch (error) {
            socket = null;
            handleError(error as Error);
            throw error;
        }
    };

    const disconnect = (): void => {
        if (reconnectTimer !== null) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }

        if (webSocketInstance && webSocketInstance.readyState !== WebSocket.CLOSED) {
            try {
                webSocketInstance.close();
            } catch (error) {
                console.error("Error closing WebSocket:", error);
            }
            webSocketInstance = null;
        }

        if (socket) {
            try {
                socket.close();
            } catch (error) {
                console.error("Error closing RSocket connection:", error);
            } finally {
                socket = null;
                updateStatus(ConnectionStatus.DISCONNECTED);
            }
        }
    };

    const getSocket = (): RSocket => {
        if (!isConnected() || !socket) {
            throw new Error("RSocket not connected");
        }
        return socket;
    };

    return {
        getStatus,
        isConnected,
        connect,
        disconnect,
        getSocket,
    };
}
